import { doc, onSnapshot, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { AppSettings, StaffAccessConfig, UserRole, MilestonePresets } from "../types";
import { DEFAULT_MILESTONE_PRESETS } from "../utils/calendarUtils";

export const DEFAULT_APP_SETTINGS: AppSettings = {
  serviceTags: [
    "Translation",
    "Localization",
    "Interpreting (VRI/OPI)",
    "On-Site Interpreting",
    "Desktop Publishing (DTP)",
    "Transcription",
    "Machine Translation Post-Editing (MTPE)",
    "Subtitling & Voiceover",
    "Staffing & Dedicated Teams",
    "Quality Assurance",
    "Compliance & HIPAA",
  ],
  defaultReviewIntervalDays: 180,
  qualifyCriteria: [
    {
      id: "experienceInArea",
      label: "Experience in Area",
      description: "Demonstrated past performance and domain expertise",
    },
    {
      id: "potentialVolume",
      label: "Potential Volume",
      description: "Estimated project volume and annual contract value",
    },
    {
      id: "effortToRespond",
      label: "Effort to Respond",
      description: "Staff time and resource investment required to produce bid",
    },
    {
      id: "securityHipaaRequirements",
      label: "Security & HIPAA",
      description: "Technical safeguards, BAAs, encryption, and compliance requirements",
    },
    {
      id: "wbePreferenceOrGoals",
      label: "WBE Preference / Goals",
      description: "Certified Women's Business Enterprise supplier diversity credits",
    },
    {
      id: "mandatoryQualifications",
      label: "Mandatory Qualifications",
      description: "Strict prerequisite certifications or minimum threshold requirements",
    },
    {
      id: "deadlineFeasibility",
      label: "Deadline Feasibility",
      description: "Feasibility of completing high-quality proposal before deadline",
    },
  ],
  pipelineStages: [
    { id: "reviewing", label: "Intake / Reviewing", color: "amber" },
    { id: "no_go", label: "Declined (No-Go)", color: "slate" },
    { id: "go", label: "Qualified (Go)", color: "teal" },
    { id: "drafting", label: "Drafting", color: "blue" },
    { id: "in_review", label: "In Review", color: "purple" },
    { id: "approved", label: "Approved", color: "indigo" },
    { id: "submitted", label: "Submitted", color: "cyan" },
    { id: "won", label: "Contract Won", color: "emerald" },
    { id: "lost", label: "Lost / Closed", color: "rose" },
  ],
  ai: {
    location: "global",
    extractionModel: "gemini-3.8-flash",
    draftingModel: "gemini-3.8-flash",
    coachModel: "gemini-3.8-flash",
    assessmentModel: "gemini-3.8-flash",
    redTeamModel: "gemini-3.8-flash",
    embeddingModel: "gemini-embedding-001",
  },
  riskSettings: {
    goThreshold: 75,
    cautionThreshold: 50,
    customKeywords: [
      "liquidated damages",
      "unlimited liability",
      "indemnification",
      "waiver of claims",
      "most favored nation",
      "intellectual property",
      "ownership of deliverables",
      "background IP",
    ],
  },
  freshnessSettings: {
    staleYearThreshold: 2,
    flagRetiredInDrafts: true,
    flagReviewDueInDrafts: true,
    flagInactiveStaffOwners: true,
  },
  effortPresets: {
    lowHours: 12,
    mediumHours: 28,
    highHours: 50,
    writingPct: 60,
    smePct: 25,
    managementPct: 15,
  },
  milestonePresets: DEFAULT_MILESTONE_PRESETS,
  staffAccess: {
    "mark@mlconnections.com": {
      email: "mark@mlconnections.com",
      role: "admin",
      hasAccess: true,
      assignedAt: "2026-01-01T00:00:00.000Z",
      assignedBy: "System Default",
    },
    "katie@mlconnections.com": {
      email: "katie@mlconnections.com",
      role: "lead",
      hasAccess: true,
      assignedAt: "2026-01-01T00:00:00.000Z",
      assignedBy: "System Default",
    },
    "meredith@mlconnections.com": {
      email: "meredith@mlconnections.com",
      role: "lead",
      hasAccess: true,
      assignedAt: "2026-01-01T00:00:00.000Z",
      assignedBy: "System Default",
    },
    "jill@mlconnections.com": {
      email: "jill@mlconnections.com",
      role: "lead",
      hasAccess: true,
      assignedAt: "2026-01-01T00:00:00.000Z",
      assignedBy: "System Default",
    },
  },
};

export const DEFAULT_STAFF_ACCESS: Record<string, StaffAccessConfig> =
  DEFAULT_APP_SETTINGS.staffAccess!;
export const DEFAULT_AI_SETTINGS = DEFAULT_APP_SETTINGS.ai!;
export const DEFAULT_RISK_SETTINGS = DEFAULT_APP_SETTINGS.riskSettings!;
export const DEFAULT_FRESHNESS_SETTINGS = DEFAULT_APP_SETTINGS.freshnessSettings!;
export const DEFAULT_EFFORT_PRESETS = DEFAULT_APP_SETTINGS.effortPresets!;
export { DEFAULT_MILESTONE_PRESETS };

const SETTINGS_DOC = "app_settings";

/**
 * Subscribes to real-time configuration settings from mlc-proposal-hq-db
 */
export function subscribeToSettings(callback: (settings: AppSettings) => void): () => void {
  const docRef = doc(db, "settings", SETTINGS_DOC);

  return onSnapshot(
    docRef,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        callback({
          serviceTags: Array.isArray(data.serviceTags)
            ? data.serviceTags
            : DEFAULT_APP_SETTINGS.serviceTags,
          defaultReviewIntervalDays:
            Number(data.defaultReviewIntervalDays) ||
            DEFAULT_APP_SETTINGS.defaultReviewIntervalDays,
          qualifyCriteria: Array.isArray(data.qualifyCriteria)
            ? data.qualifyCriteria
            : DEFAULT_APP_SETTINGS.qualifyCriteria,
          pipelineStages: Array.isArray(data.pipelineStages)
            ? data.pipelineStages
            : DEFAULT_APP_SETTINGS.pipelineStages,
          ai: {
            ...DEFAULT_AI_SETTINGS,
            ...(typeof data.ai === "object" && data.ai !== null ? data.ai : {}),
          },
          riskSettings: {
            ...DEFAULT_RISK_SETTINGS,
            ...(typeof data.riskSettings === "object" && data.riskSettings !== null
              ? data.riskSettings
              : {}),
          },
          freshnessSettings: {
            ...DEFAULT_FRESHNESS_SETTINGS,
            ...(typeof data.freshnessSettings === "object" && data.freshnessSettings !== null
              ? data.freshnessSettings
              : {}),
          },
          effortPresets: {
            ...DEFAULT_EFFORT_PRESETS,
            ...(typeof data.effortPresets === "object" && data.effortPresets !== null
              ? data.effortPresets
              : {}),
          },
          staffAccess:
            typeof data.staffAccess === "object" && data.staffAccess !== null
              ? { ...DEFAULT_STAFF_ACCESS, ...data.staffAccess }
              : DEFAULT_STAFF_ACCESS,
        });
      } else {
        // Initialize defaults if document doesn't exist
        callback(DEFAULT_APP_SETTINGS);
      }
    },
    (error) => {
      console.warn("Could not fetch Firestore settings, utilizing defaults:", error);
      callback(DEFAULT_APP_SETTINGS);
    },
  );
}

/**
 * Updates application configuration settings
 */
export async function updateSettings(updates: Partial<AppSettings>): Promise<void> {
  const docRef = doc(db, "settings", SETTINGS_DOC);
  try {
    await updateDoc(docRef, updates);
  } catch {
    // If doc doesn't exist, create it
    await setDoc(docRef, {
      ...DEFAULT_APP_SETTINGS,
      ...updates,
    });
  }
}

/**
 * Updates AI Model configuration settings with audit metadata
 */
export async function updateAiSettings(
  updates: Partial<typeof DEFAULT_AI_SETTINGS>,
  user?: { displayName?: string; email?: string },
): Promise<void> {
  const docRef = doc(db, "settings", SETTINGS_DOC);
  const aiUpdate = {
    ...DEFAULT_AI_SETTINGS,
    ...updates,
    updatedAt: new Date().toISOString(),
    updatedBy: user?.displayName || user?.email || "Admin",
  };

  try {
    await updateDoc(docRef, { ai: aiUpdate });
  } catch {
    await setDoc(
      docRef,
      {
        ...DEFAULT_APP_SETTINGS,
        ai: aiUpdate,
      },
      { merge: true },
    );
  }
}

/**
 * Updates Risk & Qualification Threshold configuration settings
 */
export async function updateRiskSettings(
  updates: Partial<typeof DEFAULT_RISK_SETTINGS>,
): Promise<void> {
  const docRef = doc(db, "settings", SETTINGS_DOC);
  const riskUpdate = {
    ...DEFAULT_RISK_SETTINGS,
    ...updates,
  };

  try {
    await updateDoc(docRef, { riskSettings: riskUpdate });
  } catch {
    await setDoc(
      docRef,
      {
        ...DEFAULT_APP_SETTINGS,
        riskSettings: riskUpdate,
      },
      { merge: true },
    );
  }
}

/**
 * Updates Freshness Scanner settings
 */
export async function updateFreshnessSettings(
  updates: Partial<typeof DEFAULT_FRESHNESS_SETTINGS>,
): Promise<void> {
  const docRef = doc(db, "settings", SETTINGS_DOC);
  const freshnessUpdate = {
    ...DEFAULT_FRESHNESS_SETTINGS,
    ...updates,
  };

  try {
    await updateDoc(docRef, { freshnessSettings: freshnessUpdate });
  } catch {
    await setDoc(
      docRef,
      {
        ...DEFAULT_APP_SETTINGS,
        freshnessSettings: freshnessUpdate,
      },
      { merge: true },
    );
  }
}

/**
 * Updates Proposal Effort Presets
 */
export async function updateEffortPresets(
  updates: Partial<typeof DEFAULT_EFFORT_PRESETS>,
): Promise<void> {
  const docRef = doc(db, "settings", SETTINGS_DOC);
  const effortUpdate = {
    ...DEFAULT_EFFORT_PRESETS,
    ...updates,
  };

  try {
    await updateDoc(docRef, { effortPresets: effortUpdate });
  } catch {
    await setDoc(
      docRef,
      {
        ...DEFAULT_APP_SETTINGS,
        effortPresets: effortUpdate,
      },
      { merge: true },
    );
  }
}

/**
 * Updates Proposal Milestone Schedule Presets (business-day offsets & timezone)
 */
export async function updateMilestonePresets(updates: Partial<MilestonePresets>): Promise<void> {
  const docRef = doc(db, "settings", SETTINGS_DOC);
  const milestoneUpdate = {
    ...DEFAULT_MILESTONE_PRESETS,
    ...updates,
  };

  try {
    await updateDoc(docRef, { milestonePresets: milestoneUpdate });
  } catch {
    await setDoc(
      docRef,
      {
        ...DEFAULT_APP_SETTINGS,
        milestonePresets: milestoneUpdate,
      },
      { merge: true },
    );
  }
}

/**
 * Updates a single staff member's access configuration and role
 */
export async function updateStaffAccess(
  email: string,
  updates: Partial<StaffAccessConfig>,
  adminUser?: { displayName?: string; email?: string },
): Promise<void> {
  const normalizedEmail = email.trim().toLowerCase();
  const docRef = doc(db, "settings", SETTINGS_DOC);

  const existingConfig = DEFAULT_STAFF_ACCESS[normalizedEmail] || {
    email: normalizedEmail,
    role: "contributor" as UserRole,
    hasAccess: false,
    assignedAt: new Date().toISOString(),
  };

  const updatedConfig: StaffAccessConfig = {
    ...existingConfig,
    ...updates,
    email: normalizedEmail,
    assignedAt: new Date().toISOString(),
    assignedBy: adminUser?.displayName || adminUser?.email || "Admin",
  };

  try {
    await updateDoc(docRef, {
      [`staffAccess.${normalizedEmail}`]: updatedConfig,
    });
  } catch {
    await setDoc(
      docRef,
      {
        ...DEFAULT_APP_SETTINGS,
        staffAccess: {
          ...DEFAULT_STAFF_ACCESS,
          [normalizedEmail]: updatedConfig,
        },
      },
      { merge: true },
    );
  }
}

/**
 * Bulk updates access and/or role for multiple staff members
 */
export async function bulkUpdateStaffAccess(
  emails: string[],
  updates: { hasAccess?: boolean; role?: UserRole },
  adminUser?: { displayName?: string; email?: string },
): Promise<void> {
  if (!emails || emails.length === 0) return;

  const docRef = doc(db, "settings", SETTINGS_DOC);
  const now = new Date().toISOString();
  const assignedBy = adminUser?.displayName || adminUser?.email || "Admin";

  const updatePayload: Record<string, unknown> = {};

  for (const email of emails) {
    const normalized = email.trim().toLowerCase();
    const existing = DEFAULT_STAFF_ACCESS[normalized] || {
      email: normalized,
      role: "contributor" as UserRole,
      hasAccess: false,
      assignedAt: now,
    };

    const nextConfig: StaffAccessConfig = {
      ...existing,
      ...(updates.hasAccess !== undefined ? { hasAccess: updates.hasAccess } : {}),
      ...(updates.role !== undefined ? { role: updates.role } : {}),
      assignedAt: now,
      assignedBy,
    };

    updatePayload[`staffAccess.${normalized}`] = nextConfig;
  }

  try {
    await updateDoc(docRef, updatePayload);
  } catch {
    const mergedStaffAccess = { ...DEFAULT_STAFF_ACCESS };
    for (const email of emails) {
      const normalized = email.trim().toLowerCase();
      const existing = DEFAULT_STAFF_ACCESS[normalized] || {
        email: normalized,
        role: "contributor" as UserRole,
        hasAccess: false,
        assignedAt: now,
      };
      mergedStaffAccess[normalized] = {
        ...existing,
        ...(updates.hasAccess !== undefined ? { hasAccess: updates.hasAccess } : {}),
        ...(updates.role !== undefined ? { role: updates.role } : {}),
        assignedAt: now,
        assignedBy,
      };
    }
    await setDoc(
      docRef,
      {
        ...DEFAULT_APP_SETTINGS,
        staffAccess: mergedStaffAccess,
      },
      { merge: true },
    );
  }
}
